import { faLink, faTimes } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Checkbox from "@/components/checkbox"
import ListButton from "@/components/list-button"
import Modal from "@/components/modal"
import FileThumbnail from "@/components/file-thumbnail"
import Space from "@/components/space"
import TextInput from "@/components/text-input"
import env from "@/env"
import { store } from "@/index"
import { useEffect, useState } from "react"
import { WithTranslation, withTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { remove, upload } from "@/redux/_upload.actions"
import { subscriptionEdit, subscriptionUpdate_AsSupervisor } from "@/redux/subscription.actions"
import { Subscription } from "@/redux/subscription.types"
import UploadButtonWidget from "./upload-button.widget"
import { STATUS_SAVED, STATUS_SAVE_ERROR, STATUS_SAVING } from "@/redux/_status.types"

interface Props extends WithTranslation {
  subscription: Subscription
  onDelete?: () => void //Show "delete" link next to the bill
}

function SubscriptionEditorWidget(props: Props) {
  const { t } = props

  const [subscription, setSubscription] = useState<Subscription>(props.subscription)
  const [status, setStatus] = useState<string>(STATUS_SAVED)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isUnlinkOpen, setIsUnlinkOpen] = useState<boolean>(false) //Confirm to unlink the Monday deal

  useEffect(() => {
    setSubscription(props.subscription)
  }, [props.subscription])

  async function deleteBill(subscription: Subscription) {
    //delete file in digitalocean space
    const response: any = await store.dispatch(remove("Bills", subscription.AccountId + "/" + subscription.id, "pdf"))
    if (response.error) {
      toast(t("bill_not_deleted"), { type: "error" })
    } else {
      saveSubscription(new Subscription({ ...subscription, bill: false }))
      handleSubscriptionChanges({ id: "bill", value: false })
      toast(t("bill_deleted"))
    }
  }

  //Change subscription value
  function handleSubscriptionChanges(event: any) {
    store.dispatch(subscriptionEdit(subscription.id, event.id, event.value))
    setSubscription(
      new Subscription({
        ...subscription,
        [event.id]: event.value
      })
    )
  }

  //Save subscription
  async function saveSubscription(subscription: Subscription) {
    setStatus(STATUS_SAVING)
    const response: any = await store.dispatch(subscriptionUpdate_AsSupervisor(subscription))
    setStatus(response.error ? STATUS_SAVE_ERROR : STATUS_SAVED)
  }

  //Unlink the Monday deal (the deal goes back to "new subscription" on the accounts board)
  async function unlinkDeal() {
    await saveSubscription(new Subscription({ ...subscription, mondayId: null }))
    handleSubscriptionChanges({ id: "mondayId", value: null })
    setIsUnlinkOpen(false)
  }

  async function uploadBill(event: any, subscription: Subscription) {
    const reader = new FileReader()
    const file = event[0]
    let fileReader: any | null

    if (file) {
      const type: string = file.type

      reader.onloadend = async (event: any) => {
        fileReader = event.target!.result

        const response: any = await store.dispatch(
          upload(subscription.id, fileReader, "Bills/" + subscription.AccountId, type)
        )
        if (response.error) {
          toast(t("file_not_uploaded"), { type: "error" })
        } else {
          saveSubscription(new Subscription({ ...subscription, bill: true }))
          handleSubscriptionChanges({ id: "bill", value: true })
          toast(t("bill_uploaded"))
        }
      }

      reader.readAsDataURL(file)
    }
  }

  return (
    <div className="rel">
      {isUnlinkOpen && (
        <Modal
          isCloseButtonVisible
          title="Délier le deal"
          onClose={() => setIsUnlinkOpen(false)}
          onNext={unlinkDeal}
          onNextCta={t("utils_confirm")}
          status={status}>
          <p className="grey-t">
            {"L'abonnement ne sera plus lié à son deal Monday. Le deal reviendra dans « Nouvel abonnement » sur la page Comptes."}
          </p>
        </Modal>
      )}

      {subscription.mondayId && (
        <div className="abs right-0 top-0">
          <ListButton icon={faLink} text="Lié à un deal Monday" onClick={() => setIsUnlinkOpen(true)} />
        </div>
      )}

      <TextInput
        id="text"
        title={t("utils_details")}
        onBlur={() => saveSubscription(subscription)}
        onChange={(e: any) => handleSubscriptionChanges(e)}
        value={subscription.text}
        isFullWidth
        status={status}
      />

      <div className="flex">
        <TextInput
          id="price"
          title={t("subscription_price")}
          onBlur={() => saveSubscription(subscription)}
          onChange={(e: any) => handleSubscriptionChanges(e)}
          value={subscription.price}
          status={status}
          type="number"
        />

        <TextInput
          id="dateStart"
          title={t("tasks_date_start")}
          onBlur={() => saveSubscription(subscription)}
          onChange={(e: any) => handleSubscriptionChanges(e)}
          value={subscription.dateStartInput}
          status={status}
          type="date"
        />

        <TextInput
          id="dateEnd"
          title={t("tasks_date_end")}
          onBlur={() => saveSubscription(subscription)}
          onChange={(e: any) => handleSubscriptionChanges(e)}
          value={subscription.dateEndInput}
          status={status}
          type="date"
        />

        <Space />

        <div className="flex flex-dcol">
          <Space />

          <Checkbox
            id="paid"
            active={!subscription.paid}
            text={t("utils_unpaid")}
            status={status}
            onClick={() => {
              saveSubscription(new Subscription({ ...subscription, paid: !subscription.paid }))
              handleSubscriptionChanges({ id: "paid", value: !subscription.paid })
            }}
          />
        </div>
      </div>

      <div className="height-10" />

      {subscription.price <= 0 && !subscription.paid && <div className="red-t">{t("unpaid_price_0")}</div>}

      <div className="height-10" />

      <div className="flex items-start gap-6">
        {subscription.bill ? (
          <div>
            <div
              className="flex"
              style={{
                width: 67
              }}>
              <div>{t("bill")}</div>

              <Space />

              <div>
                <FontAwesomeIcon icon={faTimes} onClick={() => deleteBill(subscription)} className="_hover" />
              </div>
            </div>

            <FileThumbnail
              onClick={() =>
                window.open(
                  `${env.REACT_APP_URL_SPACE}/Bills/${subscription.AccountId}/${
                    subscription.id
                  }.pdf?${new Date().getTime()}`,
                  "_blank"
                )
              }
              color="red"
              extension="pdf"
            />
          </div>
        ) : (
          <div>
            {isOpen ? (
              <div>
                <u className="_hover grey-t" onClick={() => setIsOpen(false)}>
                  {t("utils_cancel")}
                </u>
                <UploadButtonWidget
                  onUpload={(event: any) => {
                    uploadBill(event, subscription)
                  }}
                  acceptedFiles="application/pdf"
                />
              </div>
            ) : (
              <div>
                <u className="_hover grey-t" onClick={() => setIsOpen(true)}>
                  {t("bill_upload")}
                </u>
              </div>
            )}
          </div>
        )}

        {props.onDelete && (
          <u
            className="_hover grey-t"
            onClick={(e) => {
              e.stopPropagation()
              props.onDelete?.()
            }}>
            {t("subscription_delete")}
          </u>
        )}
      </div>
    </div>
  )
}

export default withTranslation()(SubscriptionEditorWidget)
